import { Badge, BadgeProps } from "@oysterhr/ui"

import {
  CustomerBankAccountMandateStateEnumType,
  B2BPaymentMethod,
  PaymentMethodState,
  CorePaymentMethod,
} from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { BadgeStateItem } from "~/shared/components/StatesDescription"
import { useTranslation } from "react-i18next"
import { isDeactivated, isFtePaymentMethod } from "../../pages/CompanyPaymentMethodsPage/utils"
type PaymentMethodStatusBadgeProps = {
  paymentMethod: CorePaymentMethod | B2BPaymentMethod
}

type State = Omit<BadgeStateItem, "descriptionKey">

const ContractorPaymentMethodStateMap: Partial<Record<PaymentMethodState, State>> = {
  [PaymentMethodState.Initialized]: {
    translationKey: "initialized",
    variation: "gray",
  },
  [PaymentMethodState.InVerification]: {
    translationKey: "initialized",
    variation: "gray",
  },
  [PaymentMethodState.Verified]: {
    translationKey: "inProgress",
    variation: "info",
  },
  [PaymentMethodState.VerificationFailed]: {
    translationKey: "verificationFailed",
    variation: "error",
  },
  [PaymentMethodState.AuthorizedByUser]: {
    translationKey: "authorizedByUser",
    variation: "warning",
  },
  [PaymentMethodState.AuthorizedForDirectDebit]: {
    translationKey: "active",
    variation: "success",
  },
}

const getContractorPaymentMethodState = (paymentMethod: CorePaymentMethod) => {
  const deactivated = isDeactivated(paymentMethod)
  if (deactivated)
    return {
      translationKey: "deactivated",
      variation: "error",
    }

  return ContractorPaymentMethodStateMap[paymentMethod.state]
}

const getFtePaymentMethodState = (customerBankAccount: B2BPaymentMethod): State => {
  switch (customerBankAccount.state) {
    case PaymentMethodState.Initialized:
      return {
        translationKey: "pendingAuthorization",
        variation: "warning",
      }
    case PaymentMethodState.Verifying:
      return {
        translationKey: "verifying",
        variation: "info",
      }
    case PaymentMethodState.VerificationFailed:
      return {
        translationKey: "verificationFailed",
        variation: "error",
      }
    case PaymentMethodState.AuthorizedForDirectDebit:
      return {
        translationKey: "active",
        variation: "success",
      }
    case PaymentMethodState.AuthorizedByUser: // If authorized, we check mandate state unless its ACH
      if (customerBankAccount.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH) {
        return {
          translationKey: "verifying",
          variation: "info",
        }
      }
      switch (customerBankAccount.mandate?.state) {
        case CustomerBankAccountMandateStateEnumType.Initialized:
          return {
            translationKey: "pendingSignature",
            variation: "warning",
          }
        case CustomerBankAccountMandateStateEnumType.Active:
          return {
            translationKey: "active",
            variation: "success",
          }
        case CustomerBankAccountMandateStateEnumType.Signed: {
          return {
            translationKey: "pendingSharing",
            variation: "warning",
          }
        }
        case CustomerBankAccountMandateStateEnumType.Lodged: {
          return {
            translationKey: "verifying",
            variation: "info",
          }
        }
        default: {
          throw new Error(
            `PaymentMethodStatusBadge - Unexpected mandate status for FTE: ${customerBankAccount.mandate?.state}`,
          )
        }
      }
    default: {
      throw new Error(
        `PaymentMethodStatusBadge - Unexpected payment method status for FTE: ${customerBankAccount.state}`,
      )
    }
  }
}

export const PaymentMethodStatusBadge = ({ paymentMethod }: PaymentMethodStatusBadgeProps) => {
  const { t } = useTranslation(
    "company_interface/CompanyPaymentMethodsPage/PaymentMethodStatusBadge",
  )

  const state = isFtePaymentMethod(paymentMethod)
    ? getFtePaymentMethodState(paymentMethod)
    : (getContractorPaymentMethodState(paymentMethod) as State)

  return (
    <Badge size="md" variation={state.variation as BadgeProps["variation"]}>
      {t(state.translationKey)}
    </Badge>
  )
}
